import { MapLayerMouseEvent } from "maplibre-gl";
import { useEffect, useRef } from "react";
import { create } from "zustand";
import { UpdatedBlocksAPIResponse } from "../components/mapping/map/edit/EditResponse";
import { handleSwap } from "../components/mapping/map/eventHandlers";
import { ZoneWithSwaps } from "../components/mapping/map/mapTypes";
import {
  PlanningBlockID,
  PlanningBlocks,
  SchoolID,
  Schools,
} from "../editing_planning/data_requirements";
import { getSwaps } from "../editing_planning/func_requirements";
import { clearEdits } from "../store/editStore";

export interface EditingContextStore {
  notification: string;
  setNotification: (notification: string) => void;
  // The schoolID which is selected for editing
  schoolSelected: SchoolID; // Rename to selectedESZone (?)
  handleClick: (
    e: MapLayerMouseEvent,
    blocks: PlanningBlocks,
    schools: Schools,
  ) => void;
  // Boolean indicating whether a school is selected or not (Possibly can be replaced by nullcheck on `schoolSelected`)
  zoneSelected: boolean;
  setZoneSelected: (zoneSelected: boolean) => void;
  // Zone which is being hovered or is selected.
  currentZone: ZoneWithSwaps | undefined;
  setCurrentZone: (currentZone: ZoneWithSwaps) => void;
  // Metric response from updates API.
  updatedMetrics: UpdatedBlocksAPIResponse;
  resetUpdatedMetrics: () => void;
  //Remove zone.
  clearZone: () => void;
  removeEdits: () => Promise<void>;

  hoveredBlock: PlanningBlockID | undefined;
  handleHoveredBlock: (
    hoveredBlock: PlanningBlockID,
    blocks?: PlanningBlocks,
    schools?: Schools,
  ) => void;
}
// TODO: We probably want to be more careful with this so we're not passing around `updatedMetrics` all the time. May be quite large. idk
export const useEditingContextStore = create<EditingContextStore>(
  (set, get) => ({
    hoveredBlock: undefined,
    updatedMetrics: undefined,
    notification: undefined,
    resetUpdatedMetrics: () => set(() => ({ updatedMetrics: undefined })),
    setNotification: (notification) =>
      set(() => ({ notification: notification })),
    schoolSelected: undefined,
    handleClick: async (
      e: MapLayerMouseEvent,
      blocks: PlanningBlocks,
      schools: Schools,
    ) => {
      const { schoolSelected, currentZone } = get();
      const clickedBlock = blocks[e.features[0].properties.pb_id];

      // If a school is not selected, select it.
      if (!schoolSelected)
        return set(() => ({
          schoolSelected: clickedBlock.es_id,
          zoneSelected: true,
          notification: "Selected zone",
        }));
      // If the current school was clicked, deselect it.
      if (clickedBlock.es_id === schoolSelected)
        return set(() => ({
          schoolSelected: undefined,
          zoneSelected: false,
          notification: "Unselected zone",
        }));
      const { newZone, updatedMetrics } = await handleSwap(
        currentZone,
        e,
        blocks,
        schools,
      );
      if (newZone && updatedMetrics)
        set(() => ({
          currentZone: newZone,
          notification: "Swap complete",
          updatedMetrics: updatedMetrics,
        }));
      else
        set(() => ({
          notification: "Invalid swap! Try another block.",
        }));
    },
    removeEdits: async () => {
      await clearEdits();
      return set(() => ({
        hoveredBlock: undefined,
        schoolSelected: undefined,
        notification: undefined,
        currentZone: undefined,
        zoneSelected: false,
      }));
    },
    zoneSelected: false,
    setZoneSelected: (zoneSelected) =>
      set(() => ({ zoneSelected: zoneSelected })),
    currentZone: undefined,
    setCurrentZone: (currentZone) => set(() => ({ currentZone: currentZone })),
    clearZone: () =>
      set(() => ({
        currentZone: undefined,
        zoneSelected: false,
        schoolSelected: undefined,
      })),
    handleHoveredBlock: (hoveredBlock, blocks, schools) => {
      const zoneSelected = get().zoneSelected;
      if (!hoveredBlock && !zoneSelected)
        return set(() => ({ hoveredBlock: undefined, currentZone: undefined }));

      if (zoneSelected) return set(() => ({ hoveredBlock: hoveredBlock }));
      const hoveredBlockObject = blocks[hoveredBlock];

      const swaps = getSwaps(
        {
          esID: hoveredBlockObject.es_id,
          msID: hoveredBlockObject.ms_id,
          hsID: hoveredBlockObject.hs_id,
        },
        blocks,
        schools,
      );
      const newCurrentZone = {
        esID: `${hoveredBlockObject.es_id}:ES`,
        msID: `${hoveredBlockObject.ms_id}:MS`,
        hsID: `${hoveredBlockObject.hs_id}:HS`,
        swaps: swaps,
      };

      if (!zoneSelected) {
        return set(() => ({
          hoveredBlock: hoveredBlock,
          currentZone: newCurrentZone,
        }));
      }
    },
  }),
);

export const useEditingContextStoreRef = () => {
  const editingContextStoreRef = useRef<EditingContextStore | null>();

  const store = useEditingContextStore();
  useEffect(() => {
    editingContextStoreRef.current = store;
  }, [store]);

  return editingContextStoreRef;
};
